import PropTypes from 'prop-types';
import { t, Trans } from '@lingui/macro';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import { MenuItem } from '@material-ui/core';
import NgpTextField from '../../form/NgpTextField';
import {
    ADMIN_ONLY_ROLES_KEY_VALUE,
    ALL_ROLES_KEY_VALUE,
    EMAIL_FIELD,
    FIRST_NAME_FIELD,
    LAST_NAME_FIELD,
    TIME_ZONE_FIELD,
    USER_DETAIL_STATUS_OPTIONS,
    USER_NAME_FIELD,
    USER_ROLE_FIELD,
    USER_SOURCE_FIELD,
    USER_STATUS_FIELD,
} from './UserConstants';
import NgpSelect from '../../form/NgpSelect';
import { AUTO_COMPLETE_DEFAULT } from '../../../utilities/FormUtilities';
import { NgpTimeZoneField } from '../../form/NgpTimeZoneField';
import NgpEmailField from '../../form/NgpEmailField';
import { useIsSysAdminUser } from '../../../stores/SessionStore';

const sourceHelpText = t`This user is managed by an external source. Various fields will be marked as read-only.`;

export default function UserDetailSection(props) {
    const { setValue, getValues } = useFormContext();
    const {
        showSourceField,
        disableStatusAndRole,
        showStatusAndRole,
        disableName,
        disableEmail,
        disableUsername,
        disableTimeZone,
    } = props;
    const [isSysAdminUser] = useIsSysAdminUser();
    const userRoleOptions = isSysAdminUser ? ALL_ROLES_KEY_VALUE : ADMIN_ONLY_ROLES_KEY_VALUE;

    const setUserEmailToUserName = (e) => {
        const email = e?.target?.value;
        const username = getValues(USER_NAME_FIELD);
        if (!_.isEmpty(email) && _.isEmpty(username)) {
            setValue(USER_NAME_FIELD, email);
        }
    };

    return (
        <>
            {showSourceField && (
                <NgpTextField
                    fieldName={USER_SOURCE_FIELD}
                    label={<Trans>Source</Trans>}
                    disabled
                    required
                    inputProps={{ size: 20 }}
                    helpTipText={sourceHelpText}
                />
            )}

            {showStatusAndRole && (
                <>
                    <NgpSelect
                        fieldName={USER_ROLE_FIELD}
                        label={<Trans>Role</Trans>}
                        disabled={disableStatusAndRole}
                        required
                    >
                        {_.map(userRoleOptions, (value, key) => (
                            <MenuItem key={key} value={key}>
                                {value}
                            </MenuItem>
                        ))}
                    </NgpSelect>

                    <NgpSelect
                        fieldName={USER_STATUS_FIELD}
                        label={<Trans>Status</Trans>}
                        disabled={disableStatusAndRole}
                        required
                    >
                        {_.map(USER_DETAIL_STATUS_OPTIONS, (value, key) => (
                            <MenuItem key={key} value={key}>
                                {value}
                            </MenuItem>
                        ))}
                    </NgpSelect>
                </>
            )}

            <NgpTextField
                fieldName={FIRST_NAME_FIELD}
                label={<Trans>First Name</Trans>}
                disabled={disableName}
                required
                inputProps={{
                    size: 35,
                    maxLength: 35,
                    autoComplete: AUTO_COMPLETE_DEFAULT,
                }}
            />

            <NgpTextField
                fieldName={LAST_NAME_FIELD}
                label={<Trans>Last Name</Trans>}
                disabled={disableName}
                required
                inputProps={{
                    size: 35,
                    maxLength: 35,
                    autoComplete: AUTO_COMPLETE_DEFAULT,
                }}
            />

            <NgpTimeZoneField
                fieldName={TIME_ZONE_FIELD}
                label={<Trans>Time Zone</Trans>}
                disabled={disableTimeZone}
                required={!disableTimeZone}
            />

            <NgpEmailField
                fieldName={EMAIL_FIELD}
                label={<Trans>Email</Trans>}
                disabled={disableEmail}
                required
                inputProps={{
                    size: 60,
                    maxLength: 60,
                    autoComplete: AUTO_COMPLETE_DEFAULT,
                }}
                onBlurFunc={setUserEmailToUserName}
            />

            <NgpTextField
                fieldName={USER_NAME_FIELD}
                label={<Trans>User Name</Trans>}
                disabled={disableUsername}
                required
                inputProps={{
                    size: 60,
                    maxLength: 60,
                    autoComplete: AUTO_COMPLETE_DEFAULT,
                }}
            />
        </>
    );
}

UserDetailSection.propTypes = {
    showSourceField: PropTypes.bool,
    disableStatusAndRole: PropTypes.bool,
    showStatusAndRole: PropTypes.bool,
    disableName: PropTypes.bool,
    disableEmail: PropTypes.bool,
    disableUsername: PropTypes.bool,
};

UserDetailSection.defaultProps = {
    showSourceField: false,
    disableStatusAndRole: false,
    showStatusAndRole: true,
    disableName: false,
    disableEmail: false,
    disableUsername: false,
};
